import { ChevronDown, X } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import SelectionActionBar from '../SelectionActionBar';
import type { SelectionAction } from '../SelectionActionBar';
import type { BillingSelectedItem } from '../../hooks/useBillingCycles';

export type BillingSelectionAction = SelectionAction<BillingSelectedItem>;

interface BillingSelectionActionBarProps {
  selectedItems: BillingSelectedItem[];
  onClear: () => void;
  onRemoveSelectedId: (id: string) => void;
  actions?: BillingSelectionAction[];
}

const getItemLabel = (item: BillingSelectedItem) =>
  item.mrn ? `${item.name} · MRN ${item.mrn}` : item.name;

function NoActionSelectionBar({
  selectedItems = [],
  onClear,
  onRemoveSelectedId,
}: Omit<BillingSelectionActionBarProps, 'actions'>) {
  if (selectedItems.length === 0) {
    return null;
  }

  return (
    <div className='fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50'>
      <div className='bg-primary text-primary-foreground px-2 py-2 rounded-lg shadow-lg flex items-center gap-2'>
        <div className='flex items-center gap-2 h-7 pl-1 bg-black/20 rounded border border-white/10'>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                className='flex items-center gap-1 px-2 h-full text-sm rounded hover:bg-black/20 transition-colors'
                aria-label={`Review ${selectedItems.length} selected ${
                  selectedItems.length === 1 ? 'item' : 'items'
                }`}
              >
                <span>{selectedItems.length} selected</span>
                <ChevronDown className='w-3.5 h-3.5' />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align='start'
              side='top'
              className='w-72 max-h-80 overflow-y-auto p-0'
            >
              <div className='py-1'>
                <div className='px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Selected billing cycles
                </div>
                {selectedItems.map(item => (
                  <div
                    key={item.id}
                    className='flex items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-accent'
                  >
                    <span className='truncate'>{getItemLabel(item)}</span>
                    <Button
                      type='button'
                      size='icon'
                      variant='ghost'
                      className='h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground'
                      onClick={() => onRemoveSelectedId(item.id)}
                      aria-label={`Remove ${getItemLabel(item)} from selection`}
                    >
                      <X className='w-3.5 h-3.5' />
                    </Button>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Button
            onClick={onClear}
            size='icon'
            variant='ghost'
            className='h-7 w-7 rounded-l-none hover:bg-black/20'
            aria-label='Clear selection'
          >
            <X className='w-3.5 h-3.5' />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function BillingSelectionActionBar({
  selectedItems = [],
  onClear,
  onRemoveSelectedId,
  actions = [],
}: BillingSelectionActionBarProps) {
  if (actions.length === 0) {
    return (
      <NoActionSelectionBar
        selectedItems={selectedItems}
        onClear={onClear}
        onRemoveSelectedId={onRemoveSelectedId}
      />
    );
  }

  return (
    <SelectionActionBar<BillingSelectedItem>
      items={selectedItems}
      onClear={onClear}
      onRemoveItem={item => onRemoveSelectedId(item.id)}
      actions={actions}
      getItemKey={item => item.id}
      getItemLabel={getItemLabel}
      getItemGroup={() => ({
        key: 'billing-cycles',
        label: 'Selected billing cycles',
      })}
    />
  );
}
